import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Lock, MessageSquare, Mail, Clock, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Multi-step qualification quiz used as the primary conversion element.
// Delivers the "check if you qualify" promise, lowers per-step friction,
// and routes leads (new businesses -> GHL nurture, not the main pipeline).

const STEPS = [
  {
    key: 'businessTenure',
    optional: true,
    question: 'How long have you been in business?',
    icon: Building2,
    options: ['Less than 1 year', '1–3 years', '3+ years'],
  },
  {
    key: 'channel',
    question: "What's the easiest way for us to share a quick overview with you?",
    icon: MessageSquare,
    options: ['Send me a text', 'Send me an email'],
  },
  {
    key: 'bestTime',
    question: "When's a good time for us to reach out — takes less than 2 minutes:",
    icon: Clock,
    options: ['Weekday morning (8–11am)', 'Weekday afternoon (12–4pm)', 'Weekday evening (5–7pm)'],
  },
];

const TOTAL = STEPS.length + 1; // + contact step

export default function EligibilityQuiz({ id }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ businessTenure: '', channel: '', bestTime: '' });
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onContact = (e) => setContact({ ...contact, [e.target.name]: e.target.value });

  const select = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));
  const skip = () => setStep((s) => s + 1);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = {};
    if (!contact.name.trim()) errs.name = 'Please enter your name';
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) errs.email = 'Enter a valid email address';
    if (contact.phone.replace(/\D/g, '').length < 7) errs.phone = 'Enter a valid phone number';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsSubmitting(true);

    // Shared id so the browser Pixel and CAPI Lead events dedupe.
    const eventId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `lead_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    // New businesses go to nurture; everyone else to the main pipeline.
    const pipeline = answers.businessTenure === 'Less than 1 year' ? 'nurture' : 'main';
    const [firstName, ...rest] = contact.name.trim().split(/\s+/);
    const lastName = rest.join(' ') || undefined;

    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', { content_name: 'Eligibility Quiz' }, { eventID: eventId });
    }

    const customData = {
      business_tenure: answers.businessTenure || undefined,
      preferred_channel: answers.channel || undefined,
      best_time: answers.bestTime || undefined,
      pipeline,
    };

    const capi = fetch('/api/meta/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: 'Lead',
        event_id: eventId,
        event_source_url: window.location.href,
        email: contact.email,
        phone: contact.phone,
        first_name: firstName || undefined,
        last_name: lastName,
        custom_data: customData,
      }),
    });

    const ghl = fetch('/api/ghl-lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: contact.name,
        first_name: firstName,
        last_name: lastName,
        email: contact.email,
        phone: contact.phone,
        business_tenure: answers.businessTenure || '',
        preferred_channel: answers.channel || '',
        best_time: answers.bestTime || '',
        pipeline,
        tags: ['website-lead', pipeline === 'nurture' ? 'nurture-new-business' : 'main-pipeline'],
        source: 'firmamkt.com landing',
        event_id: eventId,
      }),
    });

    // Attempt capture to both destinations, but never block the user from
    // reaching the booking step.
    await Promise.allSettled([capi, ghl]);

    setIsSubmitting(false);
    navigate('/thank-you', {
      state: { name: contact.name, email: contact.email, phone: contact.phone, pipeline },
    });
  };

  const isContact = step === STEPS.length;
  const current = STEPS[step];

  return (
    <div
      id={id}
      className="bg-white border border-[#E5E7EB] rounded-lg p-6 sm:p-8 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.1)]"
      data-testid="eligibility-quiz"
    >
      {/* Progress */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-[#6B7280]" data-testid="quiz-progress">
          Step {step + 1} of {TOTAL}
        </span>
        {step > 0 && (
          <button
            type="button"
            onClick={back}
            className="text-xs text-[#6B7280] hover:text-[#0A0A0A] flex items-center gap-1 transition-colors"
            data-testid="quiz-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back
          </button>
        )}
      </div>
      <div className="h-1 bg-[#E5E7EB] rounded-full mb-6 overflow-hidden">
        <div
          className="h-1 bg-[#0A0A0A] rounded-full transition-all duration-300"
          style={{ width: `${((step + 1) / TOTAL) * 100}%` }}
        />
      </div>

      {!isContact ? (
        <div data-testid={`quiz-step-${current.key}`}>
          <div className="flex items-start gap-3 mb-6">
            <current.icon className="w-6 h-6 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
            <h3 className="text-lg font-semibold text-[#0A0A0A] leading-snug">{current.question}</h3>
          </div>

          <div className="space-y-3">
            {current.options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => select(current.key, opt)}
                className="w-full text-left px-5 py-4 min-h-[56px] border border-[#E5E7EB] rounded-md hover:border-[#0A0A0A] hover:bg-[#F9FAFB] transition-colors flex items-center justify-between group"
                data-testid={`quiz-option-${current.key}`}
              >
                <span className="font-medium text-[#0A0A0A]">{opt}</span>
                <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#0A0A0A] group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>

          {current.optional && (
            <button
              type="button"
              onClick={skip}
              className="mt-4 text-sm text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
              data-testid="quiz-skip"
            >
              Skip this question
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} data-testid="quiz-contact-form" noValidate>
          <div className="flex items-start gap-3 mb-6">
            <Mail className="w-6 h-6 text-[#0A0A0A] flex-shrink-0 mt-0.5" />
            <h3 className="text-lg font-semibold text-[#0A0A0A] leading-snug">
              Where should we send your overview?
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="quiz-name" className="text-sm font-medium mb-2 block">
                Full Name <span className="text-[#0A0A0A]">*</span>
              </Label>
              <Input
                id="quiz-name"
                name="name"
                type="text"
                placeholder="Dr. John Smith"
                value={contact.name}
                onChange={onContact}
                className="input-field"
                autoComplete="name"
                data-testid="quiz-name"
              />
              {errors.name && <p className="text-xs text-red-600 mt-1" data-testid="error-name">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="quiz-email" className="text-sm font-medium mb-2 block">
                Email Address <span className="text-[#0A0A0A]">*</span>
              </Label>
              <Input
                id="quiz-email"
                name="email"
                type="email"
                placeholder="doctor@clinic.com"
                value={contact.email}
                onChange={onContact}
                className="input-field"
                autoComplete="email"
                data-testid="quiz-email"
              />
              {errors.email && <p className="text-xs text-red-600 mt-1" data-testid="error-email">{errors.email}</p>}
            </div>

            <div>
              <Label htmlFor="quiz-phone" className="text-sm font-medium mb-2 block">
                Phone Number <span className="text-[#0A0A0A]">*</span>
              </Label>
              <Input
                id="quiz-phone"
                name="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={contact.phone}
                onChange={onContact}
                className="input-field"
                autoComplete="tel"
                data-testid="quiz-phone"
              />
              {errors.phone && <p className="text-xs text-red-600 mt-1" data-testid="error-phone">{errors.phone}</p>}
            </div>

            <Button
              type="submit"
              className="btn-primary w-full mt-2"
              disabled={isSubmitting}
              data-testid="quiz-submit"
            >
              {isSubmitting ? 'Submitting...' : 'Book My Strategy Call'}
            </Button>
          </div>
        </form>
      )}

      <p className="text-xs text-[#6B7280] text-center mt-5 flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3" /> Your info is private — we only use it to prepare your call.
      </p>
    </div>
  );
}
